import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Search,
  Image as ImageIcon,
  Check,
  Loader2,
  HardDrive,
  FileImage,
} from 'lucide-react';
import clsx from 'clsx';
import { getImageUrl } from '../../lib/supabase';
import { useMediaFiles, formatFileSize } from '../../services/mediaService';
import { ImageUploader } from '../ImageUploader/ImageUploader';
import { useUploadMedia } from '../../services/mediaService';
import { debounce } from '../../lib/utils';

const overlay = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modal = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { type: 'spring', damping: 25, stiffness: 300 } },
  exit: { opacity: 0, scale: 0.95, transition: { duration: 0.15 } },
};

/**
 * Reusable media library modal for browsing/uploading/selecting images.
 * Used in "Insert to product" flow and standalone media management.
 *
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   onSelect?: (media: { id: string, file_path: string, url: string }) => void,
 *   productId?: string,
 *   selectMode?: boolean,
 * }} props
 */
export function MediaLibraryModal({
  open,
  onClose,
  onSelect,
  productId,
  selectMode = false,
}) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tab, setTab] = useState('library'); // 'library' | 'upload'
  const [selectedId, setSelectedId] = useState(null);

  const { data: files, isLoading } = useMediaFiles(debouncedSearch);
  const uploadMutation = useUploadMedia();

  const handleSearchChange = useMemo(
    () => debounce((val) => setDebouncedSearch(val), 300),
    []
  );

  const handleUpload = async (file, onProgress) => {
    const result = await uploadMutation.mutateAsync({
      file,
      productId: productId || null,
      onProgress,
    });
    // After upload, switch to library tab to see the new file
    setTab('library');
    return result;
  };

  const selectedFile = files?.find((f) => f.id === selectedId);

  const handleInsert = () => {
    if (!selectedFile || !onSelect) return;
    onSelect({
      id: selectedFile.id,
      file_path: selectedFile.file_path,
      url: getImageUrl(selectedFile.file_path),
    });
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="ml-backdrop"
            variants={overlay}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
            className="fixed inset-0 z-50 bg-brand-dark/50 backdrop-blur-sm"
          />
          <motion.div
            key="ml-modal"
            variants={modal}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-4 sm:inset-8 lg:inset-16 z-50 bg-brand-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          >
            {/* ── Header ── */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-ui-border flex-shrink-0">
              <div>
                <h2 className="text-lg font-extrabold text-brand-dark">
                  {selectMode ? 'Select Image' : 'Media Library'}
                </h2>
                <p className="text-xs text-brand-gray mt-0.5">
                  {selectMode
                    ? 'Choose an image or upload a new one'
                    : 'Browse and manage uploaded images'}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg text-brand-gray hover:bg-brand-light hover:text-brand-dark transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* ── Tabs + Search bar ── */}
            <div className="flex items-center gap-4 px-6 py-3 border-b border-ui-border flex-shrink-0">
              <div className="flex bg-brand-light rounded-xl p-0.5">
                <button
                  onClick={() => setTab('library')}
                  className={clsx(
                    'px-4 py-1.5 text-xs font-bold rounded-lg transition-all',
                    tab === 'library'
                      ? 'bg-brand-white text-brand-dark shadow-sm'
                      : 'text-brand-gray hover:text-brand-dark'
                  )}
                >
                  <span className="flex items-center gap-1.5">
                    <FileImage size={12} />
                    Library
                  </span>
                </button>
                <button
                  onClick={() => setTab('upload')}
                  className={clsx(
                    'px-4 py-1.5 text-xs font-bold rounded-lg transition-all',
                    tab === 'upload'
                      ? 'bg-brand-white text-brand-dark shadow-sm'
                      : 'text-brand-gray hover:text-brand-dark'
                  )}
                >
                  <span className="flex items-center gap-1.5">
                    <ImageIcon size={12} />
                    Upload
                  </span>
                </button>
              </div>

              {tab === 'library' && (
                <div className="relative flex-1 max-w-xs">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted" />
                  <input
                    type="text"
                    placeholder="Search images…"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      handleSearchChange(e.target.value);
                    }}
                    className="w-full pl-8 pr-4 py-2 text-sm bg-brand-light border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
                  />
                </div>
              )}

              {tab === 'library' && (
                <span className="text-xs text-brand-gray ml-auto">
                  {files?.length || 0} images
                </span>
              )}
            </div>

            {/* ── Content ── */}
            <div className="flex-1 overflow-y-auto p-6">
              {tab === 'upload' ? (
                <div className="max-w-lg mx-auto">
                  <ImageUploader
                    onUpload={handleUpload}
                    label="Upload New Image"
                  />
                </div>
              ) : isLoading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 size={28} className="animate-spin text-brand-red" />
                </div>
              ) : !files?.length ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="w-16 h-16 rounded-2xl bg-brand-light flex items-center justify-center mb-4">
                    <HardDrive size={28} className="text-ui-muted" />
                  </div>
                  <p className="text-sm font-bold text-brand-dark">No images found</p>
                  <p className="text-xs text-brand-gray mt-1">
                    {search ? 'Try a different search term' : 'Upload your first image to get started'}
                  </p>
                  <button
                    onClick={() => setTab('upload')}
                    className="mt-4 flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-brand-white bg-brand-red hover:bg-red-700 rounded-xl transition-colors"
                  >
                    <ImageIcon size={14} />
                    Upload Image
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {files.map((file) => {
                    const isSelected = selectedId === file.id;
                    const url = getImageUrl(file.file_path);
                    return (
                      <motion.button
                        key={file.id}
                        onClick={() => setSelectedId(isSelected ? null : file.id)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={clsx(
                          'relative group rounded-xl overflow-hidden border-2 transition-all aspect-square',
                          isSelected
                            ? 'border-brand-red ring-2 ring-brand-red/20'
                            : 'border-ui-border hover:border-brand-red/40'
                        )}
                      >
                        {url ? (
                          <img
                            src={url}
                            alt={file.alt_text || file.file_name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full bg-brand-light flex items-center justify-center">
                            <ImageIcon size={24} className="text-ui-muted" />
                          </div>
                        )}

                        {/* Selection check */}
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-brand-red rounded-full flex items-center justify-center shadow-md">
                            <Check size={14} className="text-brand-white" />
                          </div>
                        )}

                        {/* Overlay on hover */}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-brand-dark/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <p className="text-[10px] font-medium text-brand-white truncate">
                            {file.file_name}
                          </p>
                          <p className="text-[9px] text-brand-white/70">
                            {formatFileSize(file.file_size)}
                          </p>
                        </div>

                        {/* Linked product badge */}
                        {file.products?.title && (
                          <div className="absolute top-2 left-2 px-1.5 py-0.5 text-[9px] font-bold bg-brand-dark/60 text-brand-white rounded-md backdrop-blur-sm">
                            {file.products.title}
                          </div>
                        )}
                      </motion.button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── Footer — only in select mode when an image is selected ── */}
            {selectMode && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-ui-border flex-shrink-0 bg-brand-white">
                {selectedFile ? (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg overflow-hidden border border-ui-border">
                      <img
                        src={getImageUrl(selectedFile.file_path)}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-brand-dark truncate max-w-[200px]">
                        {selectedFile.file_name}
                      </p>
                      <p className="text-[10px] text-brand-gray">
                        {formatFileSize(selectedFile.file_size)}
                        {selectedFile.width && ` · ${selectedFile.width}×${selectedFile.height}`}
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-brand-gray">Select an image to insert</p>
                )}

                <div className="flex items-center gap-2">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-bold text-brand-gray bg-brand-light hover:bg-gray-200 rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleInsert}
                    disabled={!selectedFile}
                    className={clsx(
                      'flex items-center gap-1.5 px-4 py-2 text-sm font-bold rounded-xl transition-all',
                      selectedFile
                        ? 'text-brand-white bg-brand-red hover:bg-red-700'
                        : 'text-ui-muted bg-brand-light cursor-not-allowed'
                    )}
                  >
                    <Check size={14} />
                    Insert
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
